import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getAppApiURL } from '../utils/serverUrl';


const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [expiresTime, setExpiresTime] = useState(null);
  const isRefreshingRef = useRef(false);
  const [redirectPathCode, setRedirectPathCode] = useState(null);
  const [authProcess, setAuthProcess] = useState(null);
  const checkMockup = process.env.REACT_APP_IS_MOCKUP;

  useEffect(() => {
    if (checkMockup === "true") {
      return;
    }
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setExpiresTime(decoded.exp);
      } catch (error) {
        console.error("Failed to decode token:", error);
      }
    } /* else {
      console.error("No token found in localStorage");
    } */
  }, [token]);

  const clearToken = () => {
    setToken(null);
    setExpiresTime(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('usage_emulator_config');
  };

  const refreshToken = async () => {
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;

    try {
      const response = await fetch(`${getAppApiURL()}/api/refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          refresh_token: localStorage.getItem('refreshToken'),
          client_id: process.env.REACT_APP_CLIENT_ID,
          /* client_secret: process.env.REACT_APP_CLIENT_SECRET */
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newExpiresTime = jwtDecode(data.token).exp;
      console.table({
        now: Math.floor(new Date().getTime() / 1000).toLocaleString(),
        newExpiresTimeDate: new Date(newExpiresTime * 1000).toLocaleString(),
        diff: newExpiresTime - Math.floor(new Date().getTime() / 1000)
      })
      setToken(data.token);
      setExpiresTime(newExpiresTime);
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refresh_token);
    } catch (error) {
      console.error("Failed to refresh token", error);
      clearToken(); // Clear tokens on failure to refresh
    } finally {
      isRefreshingRef.current = false;
    }
  };

  useEffect(() => {
    const checkTokenExpiration = () => {
      if (!expiresTime || isRefreshingRef.current) {
        return;
      }

      const currentTime = Math.floor(new Date().getTime() / 1000);
      console.log("Current time:", new Date(currentTime * 1000).toLocaleString(), "Expires time:", new Date(expiresTime * 1000).toLocaleString());
      if (currentTime >= expiresTime) {
        clearToken();
        return;
      }

      // A check can land exactly five minutes before expiry. Refresh at that
      // boundary, otherwise the following five-minute check is already late.
      if (currentTime >= expiresTime - 300) {
        refreshToken();
      }
    };

    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 300000); // Check every five minutes

    return () => clearInterval(interval);
  }, [expiresTime, token]);

  return (
    <AuthContext.Provider value={{ token, setToken, redirectPathCode, setRedirectPathCode, authProcess, setAuthProcess }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
